/**
 * Customer list + slide-out edit panel (18.3).
 */

import { useState } from 'react';
import useEntityList from '../../hooks/useEntityList';
import { fetchCustomers, createCustomer, updateCustomer, deleteCustomer, restoreCustomer } from '../../api/customers';
import { SlideOutPanel, Button, LoadingSpinner, EmptyState, Pagination } from '../common';
import PanelDocuments from './PanelDocuments';

export default function CustomerList({ search, showDeleted }) {
  const { items, loading, totalCount, page, setPage, refresh } = useEntityList(fetchCustomers, { search, showDeleted });
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  function openCreate() {
    setForm({
      name: '', shipping_address_line1: '', shipping_address_line2: '',
      shipping_city: '', shipping_state: '', shipping_zip: '',
      notes: '', tags: '',
    });
    setCreating(true);
    setSelected(null);
  }

  function openEdit(item) {
    setForm({
      name: item.name || '',
      shipping_address_line1: item.shipping_address_line1 || '',
      shipping_address_line2: item.shipping_address_line2 || '',
      shipping_city: item.shipping_city || '',
      shipping_state: item.shipping_state || '',
      shipping_zip: item.shipping_zip || '',
      notes: item.notes || '',
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : (item.tags || ''),
    });
    setSelected(item);
    setCreating(false);
  }

  function closePanel() {
    setSelected(null);
    setCreating(false);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      };
      if (creating) {
        await createCustomer(payload);
      } else {
        await updateCustomer(selected.id, payload);
      }
      closePanel();
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm('Delete this customer?')) return;
    try {
      await deleteCustomer(selected.id);
      closePanel();
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed');
    }
  }

  async function handleRestore() {
    try {
      await restoreCustomer(selected.id);
      closePanel();
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Restore failed');
    }
  }

  if (loading) return <div className="flex justify-center py-10"><LoadingSpinner /></div>;

  return (
    <div>
      {/* Add button */}
      <div className="px-4 py-2 border-b border-gray-100">
        <Button size="sm" onClick={openCreate}>+ New Customer</Button>
      </div>

      {items.length === 0 ? (
        <EmptyState title="No customers" description="Add your first customer to get started." />
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">City</th>
                <th className="px-4 py-2">State</th>
                <th className="px-4 py-2">Tags</th>
                <th className="px-4 py-2 text-center">Contacts</th>
                <th className="px-4 py-2 text-center">Jobs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => openEdit(item)}
                  className={`hover:bg-blue-50 cursor-pointer ${item.deleted_at ? 'opacity-50 line-through' : ''}`}
                >
                  <td className="px-4 py-2 font-medium text-foreground">{item.name}</td>
                  <td className="px-4 py-2 text-gray-500">{item.shipping_city || '—'}</td>
                  <td className="px-4 py-2 text-gray-500">{item.shipping_state || '—'}</td>
                  <td className="px-4 py-2">
                    {(item.tags || []).map((t) => (
                      <span key={t} className="inline-block px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded mr-1">{t}</span>
                    ))}
                  </td>
                  <td className="px-4 py-2 text-center text-gray-500">{item.contacts_count ?? '—'}</td>
                  <td className="px-4 py-2 text-center text-gray-500">{item.jobs_count ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2">
            <Pagination current={page} total={Math.ceil(totalCount / 200)} onChange={setPage} />
          </div>
        </>
      )}

      {/* Slide-out panel */}
      <SlideOutPanel
        open={!!selected || creating}
        onClose={closePanel}
        title={creating ? 'New Customer' : `Edit: ${selected?.name || ''}`}
        width="max-w-lg"
      >
        <div className="space-y-4">
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Address Line 1" value={form.shipping_address_line1} onChange={(v) => setForm({ ...form, shipping_address_line1: v })} />
          <Field label="Address Line 2" value={form.shipping_address_line2} onChange={(v) => setForm({ ...form, shipping_address_line2: v })} />
          <div className="grid grid-cols-3 gap-3">
            <Field label="City" value={form.shipping_city} onChange={(v) => setForm({ ...form, shipping_city: v })} />
            <Field label="State" value={form.shipping_state} onChange={(v) => setForm({ ...form, shipping_state: v })} />
            <Field label="Zip" value={form.shipping_zip} onChange={(v) => setForm({ ...form, shipping_zip: v })} />
          </div>
          <Field label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} multiline />
          <Field label="Tags (comma-separated)" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} />

          {/* Related counts */}
          {selected && (
            <div className="text-xs text-gray-500 space-x-4">
              <span>Contacts: {selected.contacts_count ?? '—'}</span>
              <span>Jobs: {selected.jobs_count ?? '—'}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
            <Button loading={saving} onClick={handleSave}>Save</Button>
            <Button variant="ghost" onClick={closePanel}>Cancel</Button>
            {selected && !selected.deleted_at && (
              <Button variant="danger" size="sm" className="ml-auto" onClick={handleDelete}>Delete</Button>
            )}
            {selected?.deleted_at && (
              <Button variant="secondary" size="sm" className="ml-auto" onClick={handleRestore}>Restore</Button>
            )}
          </div>

          {/* Documents */}
          {selected && <PanelDocuments entityType="customer" entityId={selected.id} />}
        </div>
      </SlideOutPanel>
    </div>
  );
}

function Field({ label, value, onChange, multiline = false }) {
  const cls = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500";
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {multiline ? (
        <textarea className={cls} rows={2} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type="text" className={cls} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}
